import { z } from 'zod';
import { PaymentMethods, PaymentPlan } from '@mews-commander/data-access';
import { CustomErrorParams, CustomErrorType } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { isDefined } from '@mews/std';
import { normalize } from '@optimus-web/common';
import { CreatePaymentPlanFormField, PaymentPlanIntervalKind, ReservationForCreatePaymentPlan } from './types';

const createPaymentPlanFormSchemaBase = (
    reservations: ReservationForCreatePaymentPlan[],
    paymentPlans: PaymentPlan[]
) => createPaymentPlanFormDetailsSchemaBase(reservations, paymentPlans).merge(createPaymentPlanFirstPaymentFormSchema);

const createPaymentPlanFormDetailsSchemaBase = (
    reservations: ReservationForCreatePaymentPlan[],
    paymentPlans: PaymentPlan[]
) =>
    z.object({
        [CreatePaymentPlanFormField.PlanName]: z.string().refine(
            (planName) => {
                if (!isDefined(planName)) {
                    return false;
                }

                const normalizedPlanName = normalize(planName);

                return !paymentPlans.some(({ name }) => normalize(name) === normalizedPlanName);
            },
            {
                params: {
                    errorType: CustomErrorType.Other,
                    textKey: TextKey.CreatePaymentPlanFormDuplicatePlanNameErrorMessage,
                } satisfies CustomErrorParams,
            }
        ),
        [CreatePaymentPlanFormField.ReservationId]: z.string().refine(
            (reservationId) => {
                if (!isDefined(reservationId)) {
                    return false;
                }

                const reservation = reservations.find((r) => r.reservationId === reservationId);
                return isDefined(reservation) && reservation.isValid;
            },
            {
                params: {
                    errorType: CustomErrorType.Other,
                    textKey: TextKey.CreatePaymentPlanFormReservationInvalidErrorMessage,
                } satisfies CustomErrorParams,
            }
        ),
        [CreatePaymentPlanFormField.PaymentMethods]: z.array(z.nativeEnum(PaymentMethods)).min(1),
        [CreatePaymentPlanFormField.PaymentPlanIntervalKind]: z
            .nativeEnum(PaymentPlanIntervalKind)
            .default(PaymentPlanIntervalKind.Reservation),
    });

export const createPaymentPlanFirstPaymentFormSchema = z.object({
    [CreatePaymentPlanFormField.Amount]: z.number(),
    [CreatePaymentPlanFormField.Message]: z.string(),
    [CreatePaymentPlanFormField.Note]: z.string().optional(),
});

export const createPaymentPlanFormDetailsSchema = (
    reservations: ReservationForCreatePaymentPlan[],
    paymentPlans: PaymentPlan[]
) =>
    z.discriminatedUnion(CreatePaymentPlanFormField.PaymentPlanIntervalKind, [
        createPaymentPlanFormDetailsSchemaBase(reservations, paymentPlans).extend({
            [CreatePaymentPlanFormField.PaymentPlanIntervalKind]: z.literal(PaymentPlanIntervalKind.Reservation),
        }),
        createPaymentPlanFormDetailsSchemaBase(reservations, paymentPlans).extend({
            [CreatePaymentPlanFormField.PaymentPlanIntervalKind]: z.literal(PaymentPlanIntervalKind.Custom),
            [CreatePaymentPlanFormField.StartDateUtc]: z.string(),
        }),
    ]);

export const createPaymentPlanFormSchema = (
    reservations: ReservationForCreatePaymentPlan[],
    paymentPlans: PaymentPlan[]
) =>
    z.discriminatedUnion(CreatePaymentPlanFormField.PaymentPlanIntervalKind, [
        createPaymentPlanFormSchemaBase(reservations, paymentPlans).extend({
            [CreatePaymentPlanFormField.PaymentPlanIntervalKind]: z.literal(PaymentPlanIntervalKind.Reservation),
        }),
        createPaymentPlanFormSchemaBase(reservations, paymentPlans).extend({
            [CreatePaymentPlanFormField.PaymentPlanIntervalKind]: z.literal(PaymentPlanIntervalKind.Custom),
            [CreatePaymentPlanFormField.StartDateUtc]: z.string(),
        }),
    ]);

export type CreatePaymentPlanFormValues = z.infer<ReturnType<typeof createPaymentPlanFormSchema>>;
